/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { View, Dimensions, ScrollView, TouchableOpacity } from 'react-native';
import { formLabel } from '@assets/styles/formStyles';
import Alert from '@components/global/Alert';
import ModalFull from '@components/modal/ModalFull';
import ModalHalf from '@components/modal/ModalHalf';
import ItemDetail from '@components/global/ItemDetail';
import SociollaText from '@components/sociolla/SociollaText';
import ScrollContainer from '@components/global/ScrollContainer';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import SelectBox from '@components/form/SelectBox';
import SelectProductV2 from '@components/form/SelectProductV2';
import Image from '@components/global/Image';
import {
  Wrapper,
  FormWrapper,
  FormGroup,
  Loader,
  ButtonSticky,
} from '@components/screen/Layout';
import Colors from '@global/colors';
import { isWeb, isAndroid, isNative, isMobileWeb } from '@global/screens';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import {
  calculateSalesOrderProduct,
  getTaxList,
  parseDate,
} from '@global/helpers';

import { MsOrder } from '@api/ms-order';
import { logEvent } from '@global/analytics';

const dimension = Dimensions.get('screen');
const heightContainer = isWeb
  ? dimension.height - 200
  : isMobileWeb
  ? dimension.height - 55
  : 0;

const icon = 14;

const AddProduct = ({
  testID = 'sales-order-add-product',
  detail = {},
  show = false,
  isDetail = false,
  products = [],
  priceRulesId = '',
  RulesTaxType = '',
  orderId = '',
  regularDiscount = {},
  cashBeforeDelivery = {},
  selectedCustomer = {},
  selectedSalesTeam = {},
  activityId = '',
  onClose = () => {},
  setProducts = () => {},
  isQuotation = false,
}) => {
  const formProductRef = useRef();
  const dispatch = useDispatch();
  const showAddProduct = show ? true : false; // show or hide add product popup
  const productDetail = detail && Object.keys(detail).length ? detail : {}; // have product detail or not
  const customerSpecialDiscount = useMemo(() => {
    return selectedCustomer?.special_discount || 0;
  }, [selectedCustomer]);

  const [isLoading, setIsLoading] = useState(true);
  const [showDiscount, setShowDiscount] = useState(false);
  const [isEditDiscount, setIsEditDiscount] = useState(false);
  const [isDisc1Edited, setIsDisc1Edited] = useState(false);
  const [isDisc2Edited, setIsDisc2Edited] = useState(false);
  const [isDisc2Validating, setIsDisc2Validating] = useState(false);
  const [promotionRules, setPromotionRules] = useState([]);
  const [estimation, setEstimation] = useState({});
  const [loadingEstimation, setLoadingEstimation] = useState(false);
  const [promoParams, setPromoParams] = useState({
    skip: 0,
    limit: 10,
    initial: false,
    loadMore: false,
  });
  const [detailPromoData, setDetailPromoData] = useState(null);

  // get extra discount promo based on product selected, customer and sales team
  const getPromotionRulesList = async ({
    val = null,
    product = {},
    skip = 0,
  }) => {
    setPromoParams({
      ...promoParams,
      skip,
      ...(skip
        ? {
            loadMore: true,
          }
        : {
            initial: true,
          }),
    });
    const extraFilter = extraPromotionRulesParameters(
      product,
      selectedCustomer,
      selectedSalesTeam,
    );
    const payload = {
      params: {
        filter: { ...extraFilter },
        skip: skip,
        limit: promoParams.limit,
      },
    };
    if (val) {
      payload.params.filter.name = { $regex: val, $options: 'i' };
    }
    const response = await MsOrder.getExtraDiscount(payload);
    if (response.status == 200 && response?.data?.data?.length) {
      const old_result = JSON.parse(JSON.stringify(promotionRules));
      const result = response.data.data.map((item) => {
        if (item._id) {
          item.id = item._id;
        }
        return item;
      });
      let finalResult = result;
      if (skip > 0) {
        finalResult = old_result.concat(result);
      }
      finalResult = finalResult.map((promo) => {
        promo = handleProrateCbd(promo);
        return promo;
      });
      setPromotionRules(finalResult);
      setIsLoading(false);

      // Edit-mode: resolve discount_percentage2 from fresh list
      // Trust formik state only — productDetail is stale after qty reset or promo change
      const currentDisc2Id =
        formProductRef.current?.values?.discount_percentage2?.id;
      if (currentDisc2Id && formProductRef.current && skip === 0) {
        const match = finalResult.find(
          (p) => p._id?.toString() === currentDisc2Id?.toString(),
        );
        if (match) {
          const refreshed = handleProrateCbd(match);
          calculateTotalPrice({
            ...formProductRef.current?.values,
            discount_percentage2: refreshed,
          });
          formProductRef.current.setFieldValue(
            'discount_percentage2',
            refreshed,
          );
        }
      }

      setPromoParams({
        ...promoParams,
        skip,
        ...(skip
          ? {
              loadMore: false,
            }
          : {
              initial: false,
            }),
      });
    } else {
      if (skip <= 0) {
        setPromotionRules([]);
      }
      setPromoParams({
        ...promoParams,
        skip,
        ...(skip
          ? {
              loadMore: false,
            }
          : {
              initial: false,
            }),
      });
      setIsLoading(false);
    }
  };

  const extraPromotionRulesParameters = (product, customer, sales_team) => {
    const prorateInfo = handleProrateSpecialDiscount(regularDiscount, true);
    const cbdPercentage =
      cashBeforeDelivery?.discount_info?.deduction_percentage || 0;
    return {
      discount_info: {
        product: {
          id: product.id,
          quantity: product.quantity,
          unit_price: product.unit_price,
          discount_percentage1: {
            prorate_percentage: prorateInfo.prorate_percentage,
          },
          discount_percentage2: { cash_before_delivery: cbdPercentage },
          combination: {
            id: product.combination.id,
          },
          category: {
            id: product.category.id,
          },
          brand: {
            id: product.brand.id,
          },
        },
        customer: {
          id: customer.id,
        },
        order_id: orderId,
        brand_ids: [product.brand?.id].filter(Boolean),
        combination_ids: [product.combination?.id].filter(Boolean),
        category_ids: [product.category?.id].filter(Boolean),
        sales_team_id: sales_team?.id,
      },
    };
  };

  // calculate total price and total price before discount
  const calculateTotalPrice = (product = {}, is_edit = false) => {
    if (product && formProductRef.current) {
      if (regularDiscount?.discount_info?.deduction_percentage && !is_edit) {
        product = {
          ...product,
          discount_percentage1: handleProrateSpecialDiscount(regularDiscount),
        };
      }
      if (cashBeforeDelivery?.discount_info?.deduction_percentage && !is_edit) {
        product = {
          ...product,
          discount_percentage2: handleProrateCbd(product.discount_percentage2),
        };
      }
      const calculate = calculateSalesOrderProduct(product);
      formProductRef.current.setFieldValue(
        'total_price_before_tax',
        calculate.price,
      );
      formProductRef.current.setFieldValue(
        'total_price',
        calculate.total_price,
      );
    }
  };

  const calculateProrate = ({ percentage = 0, new_discount = 0 }) => {
    return percentage + new_discount - (percentage * new_discount) / 100;
  };

  const discountPercentages = (value) => {
    const discountPercentage = value;
    let decimalPlaces;
    if (!isNaN(discountPercentage)) {
      if (Number.isInteger(parseFloat(discountPercentage))) {
        decimalPlaces = 0;
      } else {
        decimalPlaces = 2;
      }
    }

    const formattedValue = parseFloat(value).toFixed(decimalPlaces);
    return formattedValue.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  };

  const handleProrateSpecialDiscount = (
    discount1 = {},
    prorateOnly = false,
  ) => {
    const specialDiscount =
      customerSpecialDiscount || discount1?.special_discount || 0;
    const prorateDiscount = discount1?.prorate_percentage || 0;

    let finalDiscount1 = 0;
    if (
      (discount1?._id || discount1.id) &&
      discount1?.discount_info?.deduction_percentage
    ) {
      finalDiscount1 = discount1.discount_info.deduction_percentage;
    } else if ((discount1?._id || discount1.id) && discount1?.percentage) {
      finalDiscount1 = discount1.percentage;
    }

    const finalProrate = prorateDiscount
      ? prorateDiscount
      : calculateProrate({
          percentage: finalDiscount1,
          new_discount: specialDiscount,
        });

    if (prorateOnly) {
      return {
        special_discount: specialDiscount,
        prorate_percentage: finalProrate,
      };
    } else {
      return {
        ...discount1,
        special_discount: specialDiscount,
        prorate_percentage: finalProrate,
      };
    }
  };

  const handleProrateCbd = (discount2 = {}, prorateOnly = false) => {
    const CbdDiscount =
      cashBeforeDelivery?.discount_info?.deduction_percentage ||
      discount2?.cash_before_delivery ||
      0;
    const prorateDiscount = discount2?.prorate_percentage || 0;

    let finalDiscount2 = 0;
    if (
      (discount2?._id || discount2?.id) &&
      discount2?.discount_info?.deduction_percentage
    ) {
      finalDiscount2 = discount2.discount_info.deduction_percentage;
    } else if ((discount2?._id || discount2?.id) && discount2?.percentage) {
      finalDiscount2 = discount2.percentage;
    }

    const finalProrate = prorateDiscount
      ? prorateDiscount
      : calculateProrate({
          percentage: finalDiscount2,
          new_discount: CbdDiscount,
        });

    if (prorateOnly) {
      const res = {
        cash_before_delivery: CbdDiscount,
        prorate_percentage: finalProrate,
      };
      if (discount2?.name) {
        res.name = discount2.name;
      }
      return res;
    } else {
      return {
        ...discount2,
        cash_before_delivery: CbdDiscount,
        prorate_percentage: finalProrate,
      };
    }
  };

  const resetDiscount2 = (discount2 = {}) => {
    if (discount2?.prorate_percentage || discount2?.cash_before_delivery) {
      return handleProrateCbd({}, true);
    } else {
      return '';
    }
  };

  const handleDisc2Change = async (value, oldValue, values, setFieldValue) => {
    setIsDisc2Edited(false);
    if (value?.id == oldValue?.id) {
      return;
    }

    const applyPromo = async () => {
      setIsDisc2Validating(true);
      try {
        const check = await checkPromotion(
          payloadPromotionCheck(values, value),
        );
        if (check) {
          setFieldValue('discount_percentage2', handleProrateCbd(value));
          calculateTotalPrice({ ...values, discount_percentage2: value });
        } else {
          if (oldValue?.id) {
            setFieldValue('discount_percentage2', oldValue);
          } else {
            setFieldValue(
              'discount_percentage2',
              resetDiscount2(values.discount_percentage2),
            );
          }
        }
      } finally {
        setIsDisc2Validating(false);
      }
    };

    await applyPromo();
  };

  const getEstimationOrder = async (product) => {
    try {
      if (activityId) {
        setLoadingEstimation(true);
        const productId = product?.id;
        const productCombinationId = product?.combination?.id
          ? product.combination.id
          : product.product_combination_id;
        const response = await MsOrder.getEstimationOrder(
          productId,
          productCombinationId,
          {
            params: {
              filter: {
                activity_id: activityId,
                ...(orderId
                  ? {
                      order_id: orderId,
                    }
                  : {}),
              },
            },
          },
        );
        if (response?.data?.data) {
          const result = response.data.data;
          const obj = {
            sell_out: result?.sell_out ? result.sell_out : 0,
            buffer: 0,
            current_stock: result?.current_stock ? result.current_stock : 0,
            previous_stock: result?.previous_stock ? result.previous_stock : 0,
          };
          obj.buffer = obj.sell_out > 0 ? Math.ceil(obj.sell_out / 2) : 0;
          if (
            result?.estimation_order_value ||
            result?.estimation_order_value == 0
          ) {
            obj.estimation_order_value = result.estimation_order_value;
          } else {
            obj.estimation_order_value =
              obj.sell_out + obj.buffer - obj.current_stock;
          }
          setEstimation(obj);
          setLoadingEstimation(false);
        } else {
          setEstimation({});
          setLoadingEstimation(false);
        }
      }
    } catch (error) {
      console.log('🚀🚀🚀 ~ error:', error);
    }
  };

  const checkPromotion = async (payload = {}) => {
    try {
      await MsOrder.checkPromotion(payload);
      return true;
    } catch (error) {
      dispatch(
        showAlert({
          show: true,
          isModal: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
          testID: 'sales-order-add-product-promotion-error',
        }),
      );
      return false;
    }
  };

  // add product validation
  const FormProductValidationSchema = Yup.object().shape({
    product: Yup.object().required('Product name / brand is required.'),
    quantity: Yup.string().required('Quantity is required.'),
    unit_price: Yup.string().required('Unit price is required.'),
    taxes: Yup.object().required('Taxes is required.'),
    total_price_before_tax: Yup.string().required(
      'Price before tax is required.',
    ),
    discount_percentage1_edited: Yup.number(),
    discount_percentage2_edited: Yup.number(),
  });

  const payloadPromotionCheck = (values, value) => {
    return {
      product_id: values?.product?.id,
      combination_id: values?.product?.combination?.id
        ? values?.product?.combination.id
        : values?.product?.combination_id,
      quantity: values?.quantity || 0,
      unit_price: values?.unit_price,
      discount_percentage1: values?.discount_percentage1?.id
        ? {
            id: values?.discount_percentage1?.id,
            name: values?.discount_percentage1?.name,
            percentage: values?.discount_percentage1?.percentage,
            prorate_percentage: handleProrateSpecialDiscount(
              regularDiscount,
              true,
            ).prorate_percentage,
          }
        : '',
      discount_percentage2: {
        id: value?.id,
        name: value?.name,
        percentage: value?.discount_info?.deduction_percentage || 0,
        cash_before_delivery:
          cashBeforeDelivery?.discount_info?.deduction_percentage || 0,
        budget: value?.budget || {},
      },
      order_id: orderId,
      customer: selectedCustomer,
      brand: {
        id: values?.product?.brand?.id,
      },
      category: {
        id: values?.product?.category?.id,
      },
    };
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (show) {
        setIsLoading(true);
        setEstimation({});
        if (productDetail?.id && !isDetail) {
          getEstimationOrder(productDetail);
          setTimeout(() => {
            if (formProductRef.current) {
              if (!productDetail?.discount_percentage2?.is_edit) {
                // Sync from productDetail immediately for initial display
                formProductRef.current.setFieldValue(
                  'discount_percentage2',
                  handleProrateCbd(productDetail.discount_percentage2 || {}),
                );
                // Then refetch from API to get correct tier based on current order subtotal
                // (productDetail.prorate_percentage may be stale if another product changed the tier)
                if (productDetail?.discount_percentage2?.id) {
                  getPromotionRulesList({
                    product: {
                      ...formProductRef.current.values?.product,
                      quantity: formProductRef.current.values?.quantity,
                      unit_price: formProductRef.current.values?.unit_price,
                    },
                  });
                }
              } else {
                setIsEditDiscount(true);
              }
            }
          }, 250);
          setIsLoading(false);
        } else {
          setTimeout(() => {
            setTimeout(() => {
              if (
                formProductRef.current &&
                productDetail.discount_percentage2
              ) {
                formProductRef.current.setFieldValue(
                  'discount_percentage2',
                  handleProrateCbd(productDetail.discount_percentage2, true),
                );
              }
            }, 250);
            setIsLoading(false);
          }, 750);
        }
      }
    }
    return () => {
      mounted = false;
    };
  }, [show]);

  return (
    <View testID={testID}>
      <ModalFull
        testID={`${testID}-modal`}
        title={
          isDetail
            ? `Product: ${productDetail?.name}`
            : `${productDetail?.name ? `Edit` : `Add`} Product`
        }
        show={showAddProduct && !detailPromoData}
        onClose={() => {
          setIsDisc1Edited(false);
          setIsDisc2Edited(false);
          onClose();
        }}>
        {isLoading ? (
          <View style={{ padding: 20 }}>
            <Loader />
          </View>
        ) : (
          <Formik
            innerRef={formProductRef}
            initialValues={{
              product:
                productDetail && Object.keys(productDetail).length
                  ? productDetail
                  : '',
              quantity:
                productDetail?.quantity || productDetail?.quantity == 0
                  ? productDetail.quantity
                  : '',
              unit_price: productDetail?.unit_price
                ? productDetail.unit_price
                : '',
              taxes:
                productDetail?.taxes && productDetail?.taxes?.percentage
                  ? {
                      type: productDetail.taxes?.type,
                      percentage: Number(productDetail.taxes?.percentage),
                      name: `Sales tax ${productDetail.taxes?.type}. ${productDetail.taxes?.percentage}%`,
                      id: `${productDetail.taxes?.type}-${productDetail.taxes?.percentage}`,
                    }
                  : {
                      type: '',
                      percentage: '',
                      name: '',
                      id: '',
                    },
              total_price_before_tax:
                productDetail?.total_price_before_tax ||
                productDetail?.total_price_before_tax == 0
                  ? productDetail.total_price_before_tax
                  : '',
              total_price: productDetail?.total_price
                ? productDetail.total_price
                : '',
              discount_percentage1:
                (productDetail.discount_percentage1?.percentage ||
                  productDetail.discount_percentage1?.amount ||
                  regularDiscount?.discount_info?.deduction_percentage ||
                  regularDiscount?.discount_info?.deduction_amount) &&
                !productDetail.discount_percentage1?.is_edit
                  ? {
                      id: productDetail.discount_percentage1?.id
                        ? productDetail.discount_percentage1.id
                        : regularDiscount?.id,
                      name: productDetail.discount_percentage1?.name
                        ? productDetail.discount_percentage1.name
                        : regularDiscount?.name,
                      percentage: productDetail.discount_percentage1?.percentage
                        ? productDetail.discount_percentage1.percentage
                        : regularDiscount?.discount_info?.deduction_percentage,
                      amount: productDetail.discount_percentage1?.amount
                        ? productDetail.discount_percentage1.amount
                        : regularDiscount?.discount_info?.deduction_amount,
                      ...handleProrateSpecialDiscount(
                        productDetail.discount_percentage1 || regularDiscount,
                        true,
                      ),
                    }
                  : productDetail.discount_percentage1?.is_edit
                  ? {
                      is_edit: true,
                      percentage: productDetail.discount_percentage1.percentage,
                      prorate_percentage:
                        productDetail.discount_percentage1.prorate_percentage,
                    }
                  : handleProrateSpecialDiscount({}, true),
              discount_percentage2: productDetail?.discount_percentage2?.id
                ? {
                    id: productDetail.discount_percentage2?.id,
                    name: productDetail.discount_percentage2?.name,
                    percentage:
                      productDetail.discount_percentage2?.percentage || 0,
                    amount: productDetail.discount_percentage2?.amount || 0,
                    budget: productDetail.discount_percentage2?.budget || {},
                    ...handleProrateCbd(
                      productDetail.discount_percentage2,
                      true,
                    ),
                  }
                : productDetail?.discount_percentage2?.is_edit &&
                  productDetail?.discount_percentage2.percentage
                ? {
                    is_edit: true,
                    percentage: productDetail?.discount_percentage2.percentage,
                    prorate_percentage:
                      productDetail?.discount_percentage2.prorate_percentage,
                  }
                : handleProrateCbd({}, true),
              discount_percentage1_edited: 0,
              discount_percentage2_edited: 0,
            }}
            validationSchema={FormProductValidationSchema}
            onSubmit={(values) => {
              dispatch(showPopup({ name: 'isLoading', value: true }));
              const prd =
                values.product && values.product.id
                  ? JSON.parse(JSON.stringify(values.product))
                  : {};
              const comb =
                prd.combination && Object.keys(prd.combination).length
                  ? prd.combination
                  : {};
              const img =
                comb.images && comb.images.length ? comb.images[0].url : '';
              const qty = Number(values.quantity || 0);
              if (qty <= 0) {
                dispatch(
                  showAlert({
                    show: true,
                    isModal: true,
                    title: 'Error',
                    description: 'Product quantity must be greater than 0.',
                    isConfirm: false,
                    testID: 'sales-order-add-product-qty-error',
                  }),
                );
                dispatch(showPopup({ name: 'isLoading', value: false }));
                return;
              }
              const payload = {
                id: prd.id,
                image: img,
                name: prd.name,
                category: prd.category,
                brand: prd.brand,
                combination: prd.combination,
                combination_id: prd.combination?._id,
                description: prd.description,
                how_to_use: prd.how_to_use,
                ingredients: prd.ingredients,
                quantity: qty,
                unit_price: values.unit_price,
                discount_percentage1: regularDiscount?.id
                  ? {
                      id: regularDiscount?._id,
                      name: regularDiscount?.name,
                      percentage:
                        regularDiscount?.discount_info?.deduction_percentage ||
                        0,
                      amount:
                        regularDiscount?.discount_info?.deduction_amount || 0,
                      ...handleProrateSpecialDiscount(regularDiscount, true),
                    }
                  : handleProrateSpecialDiscount(regularDiscount, true),
                discount_percentage2: values.discount_percentage2?.discount_info
                  ? {
                      id: values.discount_percentage2?._id,
                      name: values.discount_percentage2?.name,
                      percentage:
                        values.discount_percentage2?.discount_info
                          ?.deduction_percentage || 0,
                      amount:
                        values.discount_percentage2?.discount_info
                          ?.deduction_amount || 0,
                      budget: values.discount_percentage2?.budget || {},
                      ...handleProrateCbd(values.discount_percentage2, true),
                    }
                  : values.discount_percentage2?.id
                  ? {
                      id: values.discount_percentage2?.id,
                      name: values.discount_percentage2?.name,
                      percentage: values.discount_percentage2?.percentage || 0,
                      amount: values.discount_percentage2?.amount || 0,
                      budget: values.discount_percentage2?.budget || {},
                      ...handleProrateCbd(values.discount_percentage2, true),
                    }
                  : handleProrateCbd(values.discount_percentage2, true),
                total_price_before_tax: values.total_price_before_tax,
                taxes: values.taxes,
                total_price: values.total_price,
                product_type: 'order_product',
              };
              // Quantity changed
              if (Number(productDetail?.quantity) != Number(payload.quantity)) {
                payload.is_qty_changed = true;
              }
              if (
                (values.discount_percentage1_edited && isDisc1Edited) ||
                values?.discount_percentage1?.is_edit
              ) {
                payload.discount_percentage1 = {
                  is_edit: true,
                  prorate_percentage: Number(
                    discountPercentages(
                      values.discount_percentage1_edited ||
                        values?.discount_percentage1?.prorate_percentage,
                    ),
                  ),
                  percentage: Number(
                    discountPercentages(
                      values.discount_percentage1_edited ||
                        values?.discount_percentage1?.percentage,
                    ),
                  ),
                };
              }
              if (
                (values.discount_percentage2_edited && isDisc2Edited) ||
                values?.discount_percentage2?.is_edit
              ) {
                payload.discount_percentage2 = {
                  is_edit: true,
                  prorate_percentage: Number(
                    discountPercentages(
                      values.discount_percentage2_edited ||
                        values.discount_percentage2.prorate_percentage,
                    ),
                  ),
                  percentage: Number(
                    discountPercentages(
                      values.discount_percentage2_edited ||
                        values.discount_percentage2.percentage,
                    ),
                  ),
                };
              }
              if (activityId) {
                payload.sell_out = estimation?.sell_out || 0;
                payload.buffer =
                  payload.sell_out > 0 ? Math.ceil(payload.sell_out / 2) : 0;
                payload.current_stock = estimation?.current_stock || 0;
                payload.estimation_order_value =
                  estimation?.estimation_order_value || 0; // sell_out + buffer - current_stock
              }
              const pr = JSON.parse(JSON.stringify(products));
              if (
                !productDetail?.name &&
                pr.findIndex(
                  (a) =>
                    a.id == payload.id &&
                    a.combination?.id == payload.combination?.id &&
                    a.product_type == payload.product_type,
                ) > -1
              ) {
                dispatch(
                  showAlert({
                    show: true,
                    isModal: true,
                    title: 'Error',
                    description:
                      'This product has been added, please edit the product instead',
                    isConfirm: false,
                    testID: 'sales-order-add-product-duplicate',
                  }),
                );
                dispatch(showPopup({ name: 'isLoading', value: false }));
                return;
              }
              let index = -1;
              if (productDetail && Object.keys(productDetail).length) {
                const idx = pr.findIndex(
                  (a) =>
                    a.id == productDetail.id &&
                    a.combination?.id?.toString() ==
                      productDetail.combination?.id?.toString() &&
                    a.product_type == productDetail.product_type,
                );
                if (idx > -1) {
                  index = idx;
                  pr[idx] = payload;
                }
              } else {
                logEvent({
                  eventName: 'add_product_sales_order',
                  data: {
                    ...payload,
                    price_rules_id: priceRulesId,
                    order_id: orderId,
                  },
                  dataKey: 'detail',
                });
                pr.push(payload);
              }
              setProducts({ val: pr, index: index }); // set new products value
              dispatch(showPopup({ name: 'isLoading', value: false }));
              setIsDisc1Edited(false);
              setIsDisc2Edited(false);
              onClose(); // close add product popup
            }}>
            {({
              handleChange,
              setFieldValue,
              handleSubmit,
              errors,
              touched,
              values,
            }) => (
              <>
                <Wrapper fullWidth style={{ paddingBottom: isWeb ? 0 : 55 }}>
                  <ScrollView
                    style={
                      isWeb
                        ? { maxHeight: heightContainer }
                        : isMobileWeb
                        ? { height: heightContainer }
                        : {}
                    }>
                    <View
                      style={{
                        width: '100%',
                        paddingHorizontal: 10,
                        paddingVertical: 15,
                        marginBottom: isNative
                          ? isAndroid
                            ? 140
                            : 70
                          : isMobileWeb
                          ? 55
                          : 0,
                      }}>
                      <FormWrapper>
                        <View
                          style={{
                            width: '100%',
                            flexDirection: isWeb ? 'row' : 'column',
                            ...(isWeb
                              ? { justifyContent: 'space-between' }
                              : {}),
                          }}>
                          <View style={{ width: isWeb ? '48%' : '100%' }}>
                            <FormGroup>
                              <SelectProductV2
                                testID={`${testID}-product-select`}
                                label="Product Name / Brand"
                                placeholder="Select Product Name / Brand"
                                value={values.product}
                                priceRulesId={priceRulesId}
                                orderId={activityId && orderId ? orderId : ''}
                                hasError={
                                  errors.product && touched.product
                                    ? errors.product
                                    : null
                                }
                                isRequired
                                editable={!isDetail && !productDetail?.name}
                                // customFilter={
                                //   activityId ? { activity_id: activityId } : {}
                                // }
                                onChange={(value) => {
                                  const comb =
                                    value.combination &&
                                    Object.keys(value.combination).length
                                      ? value.combination
                                      : {};
                                  if (comb && Object.keys(comb).length) {
                                    const price = Number(comb.price || 0);
                                    setFieldValue('unit_price', price);
                                    calculateTotalPrice({
                                      ...values,
                                      unit_price: price,
                                    });
                                  }
                                  // if (value?.id) {
                                  //   getPromotionRulesList({ product: value });
                                  // }
                                  setFieldValue(
                                    'discount_percentage2',
                                    resetDiscount2(values.discount_percentage2),
                                  );
                                  setFieldValue('product', value);
                                  getEstimationOrder(value);
                                  setFieldValue(
                                    'taxes',
                                    getTaxList(value.vat, RulesTaxType)[0],
                                  );
                                }}
                              />
                            </FormGroup>
                            <FormGroup>
                              <Input
                                testID={`${testID}-quantity-input`}
                                label="Quantity"
                                secondLabel={
                                  activityId &&
                                  (estimation || loadingEstimation) &&
                                  (Object.keys(estimation).length ||
                                    loadingEstimation) &&
                                  !isDetail ? (
                                    <View style={{ flexDirection: 'row' }}>
                                      <SociollaText
                                        style={[
                                          formLabel,
                                          { color: Colors.grey1 },
                                        ]}>
                                        {'(Suggestion Order: '}
                                        {loadingEstimation
                                          ? 'Calculating...'
                                          : estimation?.estimation_order_value ||
                                            0}
                                        {')'}
                                      </SociollaText>
                                      {estimation &&
                                      Object.keys(estimation).length ? (
                                        <TouchableOpacity
                                          activeOpacity={0.8}
                                          onPress={() => {
                                            dispatch(
                                              showAlert({
                                                show: true,
                                                isModal: true,
                                                title: 'Information',
                                                description: [
                                                  `Sell Out: ${
                                                    estimation?.sell_out || 0
                                                  }`,
                                                  `Buffer: ${
                                                    estimation?.buffer || 0
                                                  }`,
                                                  `Current Stock: ${
                                                    estimation?.current_stock ||
                                                    0
                                                  }`,
                                                  `Previous Stock: ${
                                                    estimation?.previous_stock ||
                                                    0
                                                  }`,
                                                ],
                                                isConfirm: false,
                                                testID:
                                                  'sales-order-add-product-sell-out-info',
                                              }),
                                            );
                                          }}>
                                          <Image
                                            style={{
                                              width: icon,
                                              height: icon,
                                              marginLeft: 5,
                                              marginTop: 3,
                                            }}
                                            width={icon}
                                            height={icon}
                                            source={require('@assets/img/icons/info-blue.svg')}
                                            svg={true}
                                          />
                                        </TouchableOpacity>
                                      ) : (
                                        false
                                      )}
                                    </View>
                                  ) : (
                                    <></>
                                  )
                                }
                                value={
                                  values.quantity || values.quantity == 0
                                    ? values.quantity.toString()
                                    : ''
                                }
                                keyboardType="numeric"
                                isNumeric
                                isRequired
                                editable={!isDetail && !isQuotation}
                                hasError={
                                  errors.quantity && touched.quantity
                                    ? errors.quantity
                                    : null
                                }
                                onChangeText={(value) => {
                                  if (values?.discount_percentage2?.id) {
                                    setFieldValue(
                                      'discount_percentage2',
                                      resetDiscount2(
                                        values.discount_percentage2,
                                      ),
                                    );
                                  }
                                  setFieldValue('quantity', value);
                                  calculateTotalPrice({
                                    ...values,
                                    quantity: value,
                                    ...(values?.discount_percentage2?.id
                                      ? {
                                          discount_percentage2: resetDiscount2(
                                            values.discount_percentage2,
                                          ),
                                        }
                                      : {}),
                                  });
                                }}
                              />
                            </FormGroup>
                            <FormGroup>
                              <Input
                                testID={`${testID}-unit-price-input`}
                                label="Unit Price"
                                value={
                                  values.unit_price || values.unit_price == 0
                                    ? values.unit_price.toString()
                                    : ''
                                }
                                isNumeric
                                keyboardType="numeric"
                                isRequired
                                editable={false}
                                hasError={
                                  errors.unit_price && touched.unit_price
                                    ? errors.unit_price
                                    : null
                                }
                                onChangeText={handleChange('unit_price')}
                              />
                            </FormGroup>
                            <FormGroup>
                              <SelectBox
                                testID={`${testID}-taxes-select`}
                                label="Taxes"
                                options={getTaxList(
                                  values.product.vat,
                                  RulesTaxType,
                                )}
                                value={values.taxes}
                                // // placeholder="Select Taxes"
                                trackBy="id"
                                labelKey="name"
                                isRequired
                                editable={false}
                                hasError={
                                  errors.taxes && touched.taxes
                                    ? errors.taxes
                                    : null
                                }
                                onChange={(value) => {
                                  setFieldValue('taxes', value);
                                  calculateTotalPrice({
                                    ...values,
                                    taxes: value,
                                  });
                                }}
                              />
                            </FormGroup>
                            <FormGroup>
                              <Input
                                testID={`${testID}-price-before-tax-input`}
                                label="Price Before Tax"
                                value={
                                  values.total_price_before_tax ||
                                  values.total_price_before_tax == 0
                                    ? values.total_price_before_tax.toString()
                                    : ''
                                }
                                isRequired
                                editable={false}
                                hasError={
                                  errors.total_price_before_tax &&
                                  touched.total_price_before_tax
                                    ? errors.total_price_before_tax
                                    : null
                                }
                                onChangeText={handleChange(
                                  'total_price_before_tax',
                                )}
                              />
                            </FormGroup>
                          </View>
                          <View style={{ width: isWeb ? '48%' : '100%' }}>
                            <FormGroup style={{ position: 'relative' }}>
                              <TouchableOpacity
                                onPress={() => {
                                  setShowDiscount(true);
                                }}
                                style={{
                                  width: icon,
                                  height: icon,
                                  marginLeft: 5,
                                  marginTop: 3,
                                  position: 'absolute',
                                  right: '1%',
                                  zIndex: -1,
                                }}>
                                <Image
                                  style={{
                                    width: icon,
                                    height: icon,
                                  }}
                                  width={icon}
                                  height={icon}
                                  source={require('@assets/img/icons/info-blue.svg')}
                                  svg={true}
                                />
                              </TouchableOpacity>
                              <ModalHalf
                                title="Disc calculation"
                                animationType={isWeb ? 'fade' : 'slide'}
                                visible={showDiscount}
                                onClose={() => setShowDiscount(false)}
                                spaceFromTop={isWeb ? '20%' : false}
                                maxWidth={isWeb ? '20%' : '100%'}>
                                <ItemDetail
                                  name="P1"
                                  value={
                                    values.discount_percentage1?.percentage || 0
                                  }
                                  nameStyle={{
                                    fontSize: 15,
                                    marginLeft: 30,
                                  }}
                                  nameBold={true}
                                />
                                <ItemDetail
                                  name="P2"
                                  value={
                                    values.discount_percentage1
                                      ?.special_discount || 0
                                  }
                                  nameStyle={{
                                    fontSize: 15,
                                    marginLeft: 30,
                                  }}
                                  nameBold={true}
                                />
                                <ItemDetail
                                  name="Calculation"
                                  value="P1 + P2 - ((P1*P2)/100)"
                                  nameStyle={{
                                    fontSize: 15,
                                    marginLeft: 30,
                                  }}
                                  nameBold={true}
                                />
                              </ModalHalf>
                              <Input
                                testID={`${testID}-disc1-input`}
                                label="Disc % Percentage 1"
                                value={
                                  isDisc1Edited
                                    ? values.discount_percentage1_edited
                                    : discountPercentages(
                                        values.discount_percentage1
                                          ?.prorate_percentage,
                                      )
                                }
                                // value={values.discount_percentage1?.prorate_percentage?.toString()}
                                isNumeric={false}
                                formLabelKey="prorate_percentage"
                                keyboardType="numeric"
                                editable={
                                  selectedCustomer?.market_type?.code == 'mt'
                                    ? true
                                    : false
                                }
                                hasError={
                                  errors.discount_percentage1 &&
                                  touched.discount_percentage1
                                    ? errors.discount_percentage1
                                    : null
                                }
                                onChangeText={(value) => {
                                  setIsDisc1Edited(true);
                                  setFieldValue(
                                    'discount_percentage1_edited',
                                    value,
                                  );
                                  const objProduct = {
                                    ...values,
                                    discount_percentage1: {
                                      prorate_percentage: Number(value),
                                    },
                                  };
                                  if (isDisc2Edited) {
                                    objProduct['discount_percentage1'] = {
                                      prorate_percentage: Number(
                                        values.discount_percentage2_edited,
                                      ),
                                    };
                                  }
                                  calculateTotalPrice(objProduct, true);
                                }}
                              />
                            </FormGroup>
                            <FormGroup
                              style={
                                selectedCustomer?.market_type?.code == 'mt'
                                  ? { position: 'relative' }
                                  : {}
                              }>
                              {isEditDiscount ? (
                                <Input
                                  testID={`${testID}-disc2-input`}
                                  label="Disc % Percentage 2"
                                  value={
                                    isDisc2Edited
                                      ? values.discount_percentage2_edited
                                      : values.discount_percentage2
                                          ?.prorate_percentage
                                  }
                                  isNumeric={false}
                                  formLabelKey="prorate_percentage"
                                  keyboardType="numeric"
                                  editable={
                                    selectedCustomer?.market_type?.code == 'mt'
                                      ? true
                                      : false
                                  }
                                  hasError={
                                    errors.discount_percentage1 &&
                                    touched.discount_percentage1
                                      ? errors.discount_percentage1
                                      : null
                                  }
                                  onChangeText={(value) => {
                                    setIsDisc2Edited(true);
                                    setFieldValue(
                                      'discount_percentage2_edited',
                                      value,
                                    );
                                    const objProduct = {
                                      ...values,
                                      discount_percentage2: {
                                        prorate_percentage: Number(value),
                                      },
                                    };
                                    if (isDisc1Edited) {
                                      objProduct['discount_percentage1'] = {
                                        prorate_percentage: Number(
                                          values.discount_percentage1_edited,
                                        ),
                                      };
                                    }
                                    calculateTotalPrice(objProduct, true);
                                  }}
                                />
                              ) : (
                                <SelectBox
                                  testID={`${testID}-disc2-select`}
                                  isShowDetail={true}
                                  setDetail={(val) => setDetailPromoData(val)}
                                  isShowDetailPromo={true}
                                  label="Disc % Percentage 2"
                                  options={
                                    values?.product?.id ? promotionRules : []
                                  }
                                  value={values.discount_percentage2}
                                  placeholder="Select Disc % Percentage 2"
                                  trackBy="id"
                                  labelKey="name"
                                  formLabelKey="prorate_percentage"
                                  editable={Boolean(
                                    !isDetail &&
                                      values.product &&
                                      Number(values.quantity) > 0,
                                  )}
                                  withReset={Boolean(
                                    values.discount_percentage2?.id ||
                                      values.discount_percentage2?._id,
                                  )}
                                  withCallback={true}
                                  withLoadMore
                                  limit={promoParams.limit}
                                  loadMoreEmpty={
                                    promoParams.skip > promotionRules?.length
                                  }
                                  useCallbackOptions={[
                                    values.product,
                                    values.quantity,
                                  ]}
                                  loading={promoParams.initial}
                                  loadingCallback={promoParams.loadMore}
                                  onReset={async (oldValue) => {
                                    setFieldValue(
                                      'discount_percentage2',
                                      resetDiscount2(
                                        values.discount_percentage2,
                                      ),
                                    );
                                    calculateTotalPrice({
                                      ...values,
                                      ...(values?.discount_percentage2?.id
                                        ? {
                                            discount_percentage2:
                                              resetDiscount2(
                                                values.discount_percentage2,
                                              ),
                                          }
                                        : {}),
                                    });
                                  }}
                                  onLoadMore={(keyword) => {
                                    getPromotionRulesList({
                                      val: keyword,
                                      product: {
                                        ...values.product,
                                        quantity: values.quantity,
                                        unit_price: values.unit_price,
                                      },
                                      skip:
                                        promoParams.skip + promoParams.limit,
                                    });
                                  }}
                                  searchCallback={(keyword) => {
                                    getPromotionRulesList({
                                      val: keyword,
                                      product: {
                                        ...values.product,
                                        quantity: values.quantity,
                                        unit_price: values.unit_price,
                                      },
                                    });
                                  }}
                                  onReady={() => {
                                    getPromotionRulesList({
                                      product: {
                                        ...values.product,
                                        quantity: values.quantity,
                                        unit_price: values.unit_price,
                                      },
                                    });
                                  }}
                                  hasError={
                                    errors.discount_percentage2 &&
                                    touched.discount_percentage2
                                      ? errors.discount_percentage2
                                      : null
                                  }
                                  onChange={(value, oldValue) =>
                                    handleDisc2Change(
                                      value,
                                      oldValue,
                                      values,
                                      setFieldValue,
                                    )
                                  }
                                />
                              )}
                              {isDisc2Validating ? (
                                <SociollaText
                                  style={{
                                    color: Colors.blue0,
                                    fontSize: 11,
                                    marginTop: 8,
                                  }}>
                                  Loading...
                                </SociollaText>
                              ) : (
                                values?.discount_percentage2?.name && (
                                  <SociollaText
                                    style={{
                                      color: values?.discount_percentage2
                                        ?.is_removed_by_oos
                                        ? Colors.red0
                                        : Colors.blue0,
                                      fontSize: 11,
                                      marginTop: 8,
                                    }}>
                                    Promo: {values.discount_percentage2.name}
                                  </SociollaText>
                                )
                              )}
                            </FormGroup>
                          </View>
                        </View>
                      </FormWrapper>
                    </View>
                  </ScrollView>
                </Wrapper>
                {!isDetail ? (
                  <>
                    {isWeb ? (
                      <View
                        style={{
                          width: '100%',
                          paddingTop: 20,
                          paddingBottom: 20,
                          alignItems: 'center',
                        }}>
                        <Button
                          testID={`${testID}-save-btn`}
                          rounded
                          buttonStyle={{ width: 150 }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 14,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              Save
                            </SociollaText>
                          }
                          onPress={handleSubmit}
                        />
                      </View>
                    ) : (
                      <ButtonSticky
                        testID={`${testID}-save-btn-mobile`}
                        show
                        customHost="modal-sticky-button"
                        buttons={[
                          {
                            title: 'Save',
                            onPress: (e) => handleSubmit(e),
                          },
                        ]}
                      />
                    )}
                  </>
                ) : (
                  false
                )}
              </>
            )}
          </Formik>
        )}
        <Alert />
      </ModalFull>
      <ModalFull
        title={'Terms and Conditions'}
        show={!!detailPromoData}
        onClose={() => {
          setDetailPromoData(null);
          setIsDisc1Edited(false);
          setIsDisc2Edited(false);
          onClose();
        }}>
        <ScrollContainer
          style={{
            maxHeight: dimension.height * (isWeb ? 0.2 : 0.9),
          }}>
          <View
            style={{
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
            <SociollaText
              style={{
                fontSize: 12,
                lineHeight: 20,
                paddingBottom: 15,
                borderBottomWidth: 1,
                borderBottomColor: '#E8E8E8',
                whiteSpace: 'pre-line',
              }}>
              {detailPromoData?.description}
            </SociollaText>

            <SociollaText
              style={{
                fontSize: 10,
                lineHeight: 20,
                color: '#BDC1C9',
              }}>
              Period : {parseDate(detailPromoData?.from_date)} -{' '}
              {parseDate(detailPromoData?.to_date)}
            </SociollaText>
          </View>
        </ScrollContainer>
      </ModalFull>
    </View>
  );
};

export default AddProduct;
